/**
 * Shared helper functions for generating humanized therapist descriptions
 * Uses therapist ID for consistent variation across card and profile views
 */

interface TherapistData {
  id: number;
  name: string;
  languages?: string[];
  experience?: string | null;
  specialty?: string | null;
  approaches?: string[];
}

/**
 * Get a deterministic variation index based on therapist ID
 */
function getVariation(id: number, templateCount: number): number {
  return id % templateCount;
}

/**
 * Format a list of items into natural English (e.g., "A, B, and C")
 */
function formatList(items: string[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

/**
 * Get display name (full name, handling Dr. prefix)
 */
function getDisplayName(name: string): string {
  return name;
}

/**
 * Generate humanized language description
 */
export function getLanguageText(therapist: TherapistData): string {
  const { id, languages } = therapist;
  
  if (!languages || languages.length === 0) return "";
  
  const langList = formatList(languages);
  const displayName = getDisplayName(therapist.name);
  
  const templates = [
    `Feel free to chat in ${langList} – whatever feels most comfortable for you.`,
    `${displayName} speaks ${langList} fluently, so express yourself in the language you prefer.`,
    `Comfortable communicating in ${langList} – your choice, your comfort.`,
    `Sessions available in ${langList}. Share your thoughts in the language that feels right.`,
    `${displayName} is proficient in ${langList}. Feel at ease speaking in your preferred language.`,
  ];
  
  return templates[getVariation(id, templates.length)];
}

/**
 * Generate humanized experience description
 */
export function getExperienceText(therapist: TherapistData): string {
  const { id, name, experience } = therapist;
  
  if (!experience) return "";
  
  const displayName = getDisplayName(name);
  const yearsMatch = experience.match(/(\d+)/);
  const years = yearsMatch ? yearsMatch[1] : experience;
  
  const templates = [
    `With ${years} years of dedicated practice, ${displayName} brings deep insight to every session.`,
    `${displayName} has ${years} years of experience, offering seasoned guidance on your wellness journey.`,
    `Drawing from ${years} years in the field, ${displayName} understands the nuances of healing.`,
    `A seasoned professional with ${years} years of experience, bringing wisdom to your care.`,
    `${years} years of helping others has shaped ${displayName}'s compassionate, effective approach.`,
  ];
  
  return templates[getVariation(id + 7, templates.length)]; // offset to get different variation
}

/**
 * Generate combined specialty and approaches description
 */
export function getSpecialtyApproachesText(therapist: TherapistData): string {
  const { id, name, specialty, approaches } = therapist;
  
  const displayName = getDisplayName(name);
  const hasSpecialty = specialty && specialty.trim().length > 0;
  const hasApproaches = approaches && approaches.length > 0;
  
  if (!hasSpecialty && !hasApproaches) return "";
  
  // Only specialty
  if (hasSpecialty && !hasApproaches) {
    const templates = [
      `${displayName} specializes in ${specialty}, providing focused support in this area.`,
      `With a focus on ${specialty}, ${displayName} offers targeted guidance and care.`,
      `Expertise in ${specialty} allows ${displayName} to deeply understand your challenges.`,
    ];
    return templates[getVariation(id + 3, templates.length)];
  }
  
  // Only approaches
  if (!hasSpecialty && hasApproaches) {
    const approachList = formatList(approaches.slice(0, 4));
    const templates = [
      `${displayName} draws on ${approachList} to guide your healing journey.`,
      `Using ${approachList}, ${displayName} tailors each session to your unique needs.`,
      `Skilled in ${approachList}, bringing proven techniques to support your growth.`,
    ];
    return templates[getVariation(id + 5, templates.length)];
  }
  
  // Both specialty and approaches
  const approachList = formatList(approaches!.slice(0, 3));
  const moreApproaches = approaches!.length > 3 ? ` and ${approaches!.length - 3} more techniques` : "";
  
  const templates = [
    `Specializing in ${specialty}, ${displayName} uses ${approachList}${moreApproaches} to guide your healing journey.`,
    `With expertise in ${specialty}, ${displayName} draws on ${approachList}${moreApproaches} to support your growth.`,
    `Focused on ${specialty}, bringing skills in ${approachList}${moreApproaches} to every session.`,
    `${displayName}'s focus on ${specialty} is complemented by mastery of ${approachList}${moreApproaches}.`,
    `Combining deep knowledge of ${specialty} with ${approachList}${moreApproaches} for holistic care.`,
  ];
  
  return templates[getVariation(id + 11, templates.length)];
}

/**
 * Generate a brief intro combining key info for card view
 */
export function getCardIntroText(therapist: TherapistData): string {
  const { id, name, specialty, experience } = therapist;
  
  const displayName = getDisplayName(name);
  const yearsMatch = experience?.match(/(\d+)/);
  const years = yearsMatch ? yearsMatch[1] : null;
  
  if (!specialty && !years) return "";
  
  if (specialty && years) {
    const templates = [
      `${years} years helping with ${specialty}`,
      `Specializing in ${specialty} for ${years} years`,
      `${specialty} expert with ${years} years of practice`,
    ];
    return templates[getVariation(id + 13, templates.length)];
  }
  
  if (specialty) {
    return `Specializing in ${specialty}`;
  }
  
  if (years) {
    return `${years} years of experience`;
  }
  
  return "";
}
